import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FormDaySelector } from 'src/components/DaySelector';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { WorkoutPlan } from 'src/models/workout-plan/_types';
import {
  ImportWorkoutPlanToCalendarParams,
  bulkImportWorkoutPlanToCalendar,
  fetchPlanWorkouts,
  fetchWorkoutPlan,
} from 'src/models/workout-plan/workout-plan';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { FormWorkoutPlanSelect } from 'src/shared/Forms/Selects/WorkoutPlanSelect';
import * as Yup from 'yup';

import { useClientBulkFilters } from '../useClientBulkFilters';

interface WeekPlanImportDialogProps {
  onClose: () => void;
  onSuccess?: () => void;
  workoutPlan?: WorkoutPlan;
  onAddPlan?: (planData: any) => void;
  open?: boolean;
  clients?: any[];
  users?: any[];
  selectAll?: boolean;
}

const REPEAT_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  label: value.toString(),
  value: value.toString(),
}));

const validationSchema = Yup.object().shape({
  workoutPlanId: Yup.string().required('Required'),
  startDate: Yup.date().required('Required'),
  workoutDays: Yup.array().min(1, 'Select at least one day'),
  initialWorkoutId: Yup.string().required('Required'),
  repeatCount: Yup.string().required('Required'),
});

const WeekPlanImportForm = ({
  workoutPlan,
  onBack,
  showPlanSelection,
  setShowPlanSelection,
  isSingleClient,
}: {
  workoutPlan?: WorkoutPlan;
  onBack: () => void;
  showPlanSelection: boolean;
  setShowPlanSelection: (show: boolean) => void;
  isSingleClient: boolean;
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.batchActions',
  });
  const { values, setFieldValue, validateField, handleSubmit } =
    useFormikContext<ImportWorkoutPlanToCalendarParams>();

  // Get the selected plan's details
  const { data: selectedPlan } = useQuery({
    queryKey: ['workoutPlan', values.workoutPlanId],
    queryFn: () => fetchWorkoutPlan({ id: values.workoutPlanId }),
    enabled: !!values.workoutPlanId,
  });

  // Get workouts for the selected plan
  const { data: planWorkouts = [], isSuccess: planWorkoutsSuccess } = useQuery({
    queryKey: ['planWorkouts', values.workoutPlanId],
    queryFn: () => fetchPlanWorkouts({ planId: values.workoutPlanId }),
    enabled: !!values.workoutPlanId,
  });

  useEffect(() => {
    if (planWorkoutsSuccess && planWorkouts) {
      if (planWorkouts.length > 0 && !values.initialWorkoutId) {
        setFieldValue('initialWorkoutId', planWorkouts[0].id.toString());
      }
    }
  }, [
    planWorkouts,
    planWorkoutsSuccess,
    values.initialWorkoutId,
    setFieldValue,
  ]);

  const currentPlan = workoutPlan || selectedPlan;
  const dialogTitle = currentPlan
    ? t('importNamedSequence', { name: currentPlan.name })
    : t('importWeekPlan');

  const handlePlanChange = (value: string) => {
    setFieldValue('workoutPlanId', value);
  };

  const handleNext = async () => {
    if (!values.workoutPlanId) {
      validateField('workoutPlanId');
      return;
    }

    if (currentPlan?.days) {
      setFieldValue('workoutDays', currentPlan.days);
    }
    // Store the plan name for later use
    if (currentPlan?.name) {
      setFieldValue('workoutPlanName', currentPlan.name);
    }
    setShowPlanSelection(false);
  };

  const renderPlanSelection = () => (
    <Stack spacing={2} key="plan-selection">
      <Typography>{t('workoutPlanSelectionNote')}</Typography>
      <FormWorkoutPlanSelect
        name="workoutPlanId"
        label={t('selectWorkoutPlan')}
        disabled={!!workoutPlan}
        onChange={handlePlanChange}
      />
    </Stack>
  );

  const renderImportDetails = () => (
    <Stack spacing={3} key="import-details">
      <Typography variant="h6">{t('chooseStartDate')}:</Typography>
      <FormDatePicker name="startDate" label={t('startDate')} />

      <Typography variant="h6">{t('chooseDays')}:</Typography>
      <FormDaySelector name="workoutDays" label={t('workoutDays')} />
      <FormCheckbox
        name="preventNextWorkout"
        label={t('requireLoggingBeforeNext')}
      />

      <Typography variant="h6">{t('chooseFirstWorkout')}:</Typography>
      <FormTextField
        name="initialWorkoutId"
        label={t('firstWorkout')}
        options={planWorkouts.map((workout: any) => ({
          label: workout.name,
          value: workout.id.toString(),
        }))}
      />

      <Typography variant="h6" key="scheduling-details">
        {t('schedulingDetails')}:
      </Typography>
      <FormTextField
        name="repeatCount"
        label={t('repeatSequenceQuestion')}
        options={REPEAT_OPTIONS}
      />

      <FormCheckbox
        name="notifyClient"
        label={t('sendClientEmailNotification')}
      />

      {!currentPlan?.calendarGroupId && !isSingleClient && (
        <FormCheckbox name="assignPlan" label={t('assignPlanToClient')} />
      )}

      {currentPlan?.nextPlanId && (
        <FormCheckbox
          name="scheduleNext"
          label={t('scheduleNextPlanAutomatically')}
        />
      )}
    </Stack>
  );

  return (
    <Dialog
      open
      onClose={onBack}
      title={dialogTitle}
      leftAdornment={
        !workoutPlan && !showPlanSelection ? (
          <IconButton
            onClick={() => {
              setShowPlanSelection(true);
              setFieldValue('workoutPlanId', '');
            }}
          >
            <Icon icon={faArrowLeft} />
          </IconButton>
        ) : undefined
      }
      actions={[
        { label: t('cancel'), onClick: onBack },
        ...(showPlanSelection
          ? [
              {
                label: t('next'),
                disabled: !values.workoutPlanId,
                onClick: handleNext,
              },
            ]
          : [
              {
                label: isSingleClient ? t('add') : t('import'),
                onClick: () => handleSubmit(),
              },
            ]),
      ]}
    >
      {showPlanSelection ? renderPlanSelection() : renderImportDetails()}
    </Dialog>
  );
};

export const WeekPlanImportDialog = ({
  clients,
  selectAll,
  onClose,
  onSuccess,
  workoutPlan,
  onAddPlan,
  open,
}: WeekPlanImportDialogProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.batchActions',
  });
  const { filters } = useClientBulkFilters(!!selectAll);
  const [showPlanSelection, setShowPlanSelection] = useState(!workoutPlan);

  // Detect single vs batch mode
  const isSingleClient = !clients;

  const { mutateAsync: bulkImportPlan } = useMutation({
    mutationFn: bulkImportWorkoutPlanToCalendar,
    onSuccess: () => {
      toast.success(t('plansBeingImported'));
      onSuccess?.();
    },
  });

  const initialValues = useMemo(
    () => ({
      workoutPlanId: workoutPlan?.id.toString() || '',
      workoutPlanName: workoutPlan?.name || '',
      startDate: new Date(),
      workoutDays: workoutPlan?.days || [],
      initialWorkoutId: '',
      repeatCount: 1,
      preventNextWorkout: false,
      notifyClient: true,
      assignPlan: false,
      scheduleNext: true,
      userId: '',
    }),
    [workoutPlan]
  );

  const handleSubmit = async (
    values: ImportWorkoutPlanToCalendarParams & { workoutPlanName?: string }
  ) => {
    if (!values.workoutPlanId) return;

    if (isSingleClient && onAddPlan) {
      // Single client mode - return plan data to parent component
      const planData = {
        plan_to_copy_id: values.workoutPlanId,
        plan_to_copy_name: values.workoutPlanName || '',
        start_date: new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).format(values.startDate),
        initial_workout_id: values.initialWorkoutId,
        days: values.workoutDays,
        schedule_next: values.scheduleNext,
        repeat_count: parseInt(values.repeatCount.toString(), 10),
        notify: values.notifyClient,
        prevent_next_workout: values.preventNextWorkout,
        assign_plan: values.assignPlan,
        exercise_swaps: '[]',
      };
      onAddPlan(planData);
      // Reset the modal state for next use
      setShowPlanSelection(true);
      onClose();
    } else if (clients) {
      // Batch mode - use existing bulk import
      await bulkImportPlan({
        ...values,
        planIds: clients.map((client) => client.calendarClientId.toString()),
        selectAll: selectAll || false,
        filters,
        startDate: values.startDate,
        repeatCount: parseInt(values.repeatCount.toString(), 10),
      });

      onSuccess?.();
      onClose();
    }
  };

  // Return null if modal should not be displayed (for single-client mode with open prop)
  if (isSingleClient && open === false) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <WeekPlanImportForm
        workoutPlan={workoutPlan}
        onBack={onClose}
        showPlanSelection={showPlanSelection}
        setShowPlanSelection={setShowPlanSelection}
        isSingleClient={isSingleClient}
      />
    </Formik>
  );
};
